import React, { useState } from 'react'
import { AlertCircle, ChevronDown, ChevronRight, GripVertical, Plus, Trash2 } from 'lucide-react'
import Button from '../../ui/Button'
import Input from '../../ui/Input'
import Select from '../../ui/Select'
import type { AppendixCodeItemOption, AppendixColumnDataType } from '../../../types/codeIndex'
import {
  APPENDIX_COLUMN_TYPE_OPTIONS,
  AppendixColumnDraft,
  AppendixColumnError,
  columnHasUnit,
  formatColumnPillMeta,
  formatCount,
} from '../../../utils/appendixTable'

type AppendixColumnsSectionProps = {
  columns: AppendixColumnDraft[]
  codeItems: AppendixCodeItemOption[]
  columnErrors: Record<string, AppendixColumnError>
  firstColumnError: string | null
  expanded: boolean
  autoFocusColumnKey: string | null
  disabled?: boolean
  readOnly?: boolean
  onToggleExpanded: () => void
  onAddColumn: () => void
  onUpdateColumn: (columnKey: string, patch: Partial<Pick<AppendixColumnDraft, 'label' | 'unit'>>) => void
  onRequestTypeChange: (columnKey: string, dataType: AppendixColumnDataType, codeItemCode?: string) => void
  onDeleteColumn: (columnKey: string) => void
  onMoveColumn: (fromIndex: number, toIndex: number) => void
}

const FIELD_LABEL_CLASS = 'mb-1 block text-xs font-medium text-gray-600'

export const CodeItemChip: React.FC<{ code: string; tone?: 'navy' | 'light' }> = ({ code, tone = 'navy' }) => (
  <span
    className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold leading-none ${tone === 'navy' ? 'bg-[#1D5176] text-white' : 'bg-[#DCEBFA] text-[#1D5176]'}`}
  >
    {code}
  </span>
)

const ColumnPill: React.FC<{ column: AppendixColumnDraft; index: number }> = ({ column, index }) => {
  const label = column.label.trim() || `Column ${index + 1}`

  if (column.dataType === 'CodeItem') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF6FF] px-2.5 py-1 text-xs">
        {column.codeItemCode ? <CodeItemChip code={column.codeItemCode} /> : null}
        <span className="font-medium text-[#1D74D8]">{label}</span>
      </span>
    )
  }

  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F1F5F9] px-2.5 py-1 text-xs">
      <span className="font-medium text-gray-800">{label}</span>
      <span className="text-gray-500">{formatColumnPillMeta(column)}</span>
    </span>
  )
}

export const AppendixColumnsSection: React.FC<AppendixColumnsSectionProps> = ({
  columns,
  codeItems,
  columnErrors,
  firstColumnError,
  expanded,
  autoFocusColumnKey,
  disabled = false,
  readOnly = false,
  onToggleExpanded,
  onAddColumn,
  onUpdateColumn,
  onRequestTypeChange,
  onDeleteColumn,
  onMoveColumn,
}) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [dropIndex, setDropIndex] = useState<number | null>(null)
  const inputsDisabled = disabled || readOnly

  const endDrag = () => {
    setDraggedIndex(null)
    setDropIndex(null)
  }

  return (
    <section className="rounded-lg border border-[#E2E8F0]">
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-expanded={expanded}
          className="flex min-w-0 cursor-pointer items-center gap-2 text-left"
        >
          {expanded
            ? <ChevronDown className="h-4 w-4 flex-shrink-0 text-[#1D5176]" aria-hidden="true" />
            : <ChevronRight className="h-4 w-4 flex-shrink-0 text-[#1D5176]" aria-hidden="true" />}
          <span className="text-sm font-semibold text-gray-900">Columns</span>
          <span className="text-sm text-gray-500">
            {columns.length > 0 ? formatCount(columns.length, 'column') : 'No columns yet'}
          </span>
        </button>
        {!readOnly ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="bg-[#EEF6FF]"
            onClick={onAddColumn}
            disabled={disabled}
            leftIcon={<Plus className="h-4 w-4" aria-hidden="true" />}
          >
            Add Column
          </Button>
        ) : null}
      </div>

      {expanded ? (
        <div className="space-y-2 px-4 pb-4">
          {columns.length === 0 ? (
            <p className="text-sm text-gray-500">Define the structure before entering rows.</p>
          ) : (
            columns.map((column, index) => {
              const position = index + 1
              const error = columnErrors[column.key]
              const isDropTarget = dropIndex === index && draggedIndex !== null && draggedIndex !== index

              return (
                <div
                  key={column.key}
                  data-testid="appendix-column-card"
                  onDragOver={(event) => {
                    if (draggedIndex === null) return
                    event.preventDefault()
                    setDropIndex(index)
                  }}
                  onDrop={(event) => {
                    event.preventDefault()
                    if (draggedIndex !== null) onMoveColumn(draggedIndex, index)
                    endDrag()
                  }}
                  className={`grid grid-cols-[20px_minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)_32px] items-end gap-3 rounded-md border px-3 py-2 transition-colors focus-within:border-[#1D74D8] focus-within:bg-[#F1F7FD] ${isDropTarget ? 'border-[#1D74D8] bg-[#F1F7FD]' : 'border-[#E2E8F0] bg-white'} ${draggedIndex === index ? 'opacity-60' : ''}`}
                >
                  {readOnly ? <span /> : (
                    <span
                      draggable={!disabled}
                      onDragStart={(event) => {
                        event.dataTransfer.effectAllowed = 'move'
                        event.dataTransfer.setData('text/plain', column.key)
                        const card = event.currentTarget.parentElement
                        if (card && typeof event.dataTransfer.setDragImage === 'function') {
                          event.dataTransfer.setDragImage(card, 16, 16)
                        }
                        setDraggedIndex(index)
                      }}
                      onDragEnd={endDrag}
                      aria-label={`Drag column ${position}`}
                      className="flex h-8 cursor-grab items-center justify-center text-gray-300 hover:text-gray-500"
                    >
                      <GripVertical className="h-4 w-4" aria-hidden="true" />
                    </span>
                  )}

                  <div className="min-w-0">
                    <label className={FIELD_LABEL_CLASS} htmlFor={`${column.key}-label`}>Label</label>
                    <Input
                      id={`${column.key}-label`}
                      value={column.label}
                      invalid={error?.field === 'label'}
                      autoFocus={column.key === autoFocusColumnKey}
                      aria-label={`Column ${position} label`}
                      onChange={(event) => onUpdateColumn(column.key, { label: event.target.value })}
                      disabled={inputsDisabled}
                    />
                  </div>

                  <div className="min-w-0">
                    <label className={FIELD_LABEL_CLASS} htmlFor={`${column.key}-type`}>Type</label>
                    <Select
                      id={`${column.key}-type`}
                      value={column.dataType}
                      aria-label={`Column ${position} type`}
                      onChange={(event) => onRequestTypeChange(
                        column.key,
                        event.target.value as AppendixColumnDataType,
                        column.codeItemCode
                      )}
                      disabled={inputsDisabled}
                    >
                      {APPENDIX_COLUMN_TYPE_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </Select>
                  </div>

                  <div className="min-w-0">
                    {column.dataType === 'CodeItem' ? (
                      <>
                        <label className={FIELD_LABEL_CLASS} htmlFor={`${column.key}-code-item`}>Which code item</label>
                        <Select
                          id={`${column.key}-code-item`}
                          value={column.codeItemCode}
                          invalid={error?.field === 'codeItem'}
                          aria-label={`Column ${position} code item`}
                          onChange={(event) => onRequestTypeChange(column.key, 'CodeItem', event.target.value)}
                          disabled={inputsDisabled}
                        >
                          <option value="">Select</option>
                          {codeItems.map(codeItem => (
                            <option key={codeItem.id} value={codeItem.code}>
                              {codeItem.title ? `${codeItem.code} - ${codeItem.title}` : codeItem.code}
                            </option>
                          ))}
                        </Select>
                      </>
                    ) : (
                      <>
                        <label className={FIELD_LABEL_CLASS} htmlFor={`${column.key}-unit`}>Unit</label>
                        {columnHasUnit(column.dataType) ? (
                          <Input
                            id={`${column.key}-unit`}
                            value={column.unit}
                            placeholder="Eg. MBh"
                            aria-label={`Column ${position} unit`}
                            onChange={(event) => onUpdateColumn(column.key, { unit: event.target.value })}
                            disabled={inputsDisabled}
                          />
                        ) : (
                          <Input id={`${column.key}-unit`} value="—" aria-label={`Column ${position} unit`} disabled readOnly />
                        )}
                      </>
                    )}
                  </div>

                  {readOnly ? <span /> : (
                    <button
                      type="button"
                      onClick={() => onDeleteColumn(column.key)}
                      disabled={disabled}
                      aria-label={`Remove column ${position}`}
                      className="flex h-8 w-8 cursor-pointer items-center justify-center rounded text-[#1D5176] hover:bg-[#EEF6FF] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  )}
                </div>
              )
            })
          )}
          {firstColumnError ? (
            <p role="alert" className="flex items-center gap-1.5 text-xs text-[#B3261E]">
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
              {firstColumnError}
            </p>
          ) : null}
        </div>
      ) : columns.length > 0 ? (
        <div className="flex flex-wrap gap-2 px-4 pb-4" aria-label="Column summary">
          {columns.map((column, index) => (
            <ColumnPill key={column.key} column={column} index={index} />
          ))}
        </div>
      ) : null}
    </section>
  )
}

export default AppendixColumnsSection
